// src/components/common/DatePicker.jsx
import React, { useEffect, useRef, useState } from "react";
import { CalendarDays } from "lucide-react";

/**
 * DatePicker (reusable)
 *
 * Props:
 *  - label: string (optional)
 *  - value: string (ISO YYYY-MM-DD) or empty string
 *  - onChange: function({ target: { value } })  -- value is YYYY-MM-DD or "" when cleared
 *  - id: string (optional)
 *  - allowFuture: boolean (default: false)  -> allow selecting future dates
 *  - showAge: boolean (default: false) -> show age under the input after selection
 *  - placeholder: string (default "dd-mm-yyyy") - visual placeholder, value still in YYYY-MM-DD
 *  - className: string (optional)
 *
 * Behaviour matches the working Settings.jsx calendar UI: month grid, year grid (12 years),
 * Clear + Today actions, disabled future dates when allowFuture=false, today highlight,
 * selected day highlight, year-mode toggle by clicking the month label.
 */
export default function DatePicker({
  label,
  value = "",
  onChange,
  id,
  allowFuture = false,
  showAge = false,
  placeholder = "dd-mm-yyyy",
  className = "",
}) {
  const now = new Date();
  const maxDateDefault = allowFuture ? new Date(9999, 11, 31) : new Date();

  // internal UI state
  const [open, setOpen] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(
    value ? new Date(value) : new Date()
  );
  const [yearMode, setYearMode] = useState(false);
  const [yearRangeStart, setYearRangeStart] = useState(null);
  const [dropUp, setDropUp] = useState(false);

  const containerRef = useRef(null);

  // sync calendarMonth when external value changes
  useEffect(() => {
    if (value) {
      const d = new Date(value);
      if (!Number.isNaN(d.getTime())) setCalendarMonth(d);
    }
  }, [value]);

  // close on outside click
  useEffect(() => {
    function onDoc(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setYearMode(false);
      }
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Measure space for dropup
  useEffect(() => {
    if (open && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      // Datepicker needs roughly 350px
      if (spaceBelow < 350 && rect.top > 350) {
        setDropUp(true);
      } else {
        setDropUp(false);
      }
    }
  }, [open]);

  // helpers
  function toYYYYMMDD(d) {
    if (!d) return "";
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }

  function formatDisplay(iso) {
    if (!iso) return "";
    const dt = new Date(iso);
    if (Number.isNaN(dt.getTime())) return "";
    const dd = String(dt.getDate()).padStart(2, "0");
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const yyyy = dt.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  }

  // generate days for calendar month
  function getCalendarDays(date) {
    const y = date.getFullYear();
    const m = date.getMonth();
    const first = new Date(y, m, 1);
    const last = new Date(y, m + 1, 0);
    const pad = first.getDay(); // 0..6
    const total = last.getDate();
    const arr = [];
    for (let i = 0; i < pad; i++) arr.push(null);
    for (let d = 1; d <= total; d++) arr.push(d);
    return arr;
  }

  // year range window for year-mode (12 years, centered)
  function computeYearRange(centerYear) {
    return centerYear - 6; // start such that center year is near middle (like your Settings.jsx)
  }

  function enterYearMode() {
    const cy = calendarMonth.getFullYear();
    const start = computeYearRange(cy);
    setYearRangeStart(start);
    setYearMode(true);
  }

  // selection helpers
  const selectedDate = value ? new Date(value) : null;
  function isSelected(day) {
    if (!selectedDate) return false;
    return (
      selectedDate.getFullYear() === calendarMonth.getFullYear() &&
      selectedDate.getMonth() === calendarMonth.getMonth() &&
      selectedDate.getDate() === day
    );
  }

  function isToday(day) {
    const d = new Date(
      calendarMonth.getFullYear(),
      calendarMonth.getMonth(),
      day
    );
    return (
      d.getFullYear() === now.getFullYear() &&
      d.getMonth() === now.getMonth() &&
      d.getDate() === now.getDate()
    );
  }

  function canSelect(day) {
    if (day === null) return false;
    const d = new Date(
      calendarMonth.getFullYear(),
      calendarMonth.getMonth(),
      day
    );
    return allowFuture || d <= maxDateDefault;
  }

  function pickDay(day) {
    if (!canSelect(day)) return;
    const d = new Date(
      calendarMonth.getFullYear(),
      calendarMonth.getMonth(),
      day
    );
    const iso = toYYYYMMDD(d);
    onChange && onChange({ target: { value: iso } });
    setOpen(false);
  }

  function clearDate() {
    onChange && onChange({ target: { value: "" } });
    setOpen(false);
  }

  function pickToday() {
    const d = new Date();
    const iso = toYYYYMMDD(d);
    onChange && onChange({ target: { value: iso } });
    setCalendarMonth(d);
    setOpen(false);
  }

  // month/year navigation
  function prevMonth() {
    setCalendarMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1));
  }
  function nextMonth() {
    setCalendarMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1));
  }
  function prevYear() {
    setCalendarMonth((m) => new Date(m.getFullYear() - 1, m.getMonth(), 1));
  }
  function nextYear() {
    setCalendarMonth((m) => new Date(m.getFullYear() + 1, m.getMonth(), 1));
  }

  function selectYear(year) {
    setCalendarMonth((m) => new Date(year, m.getMonth(), 1));
    setYearMode(false);
  }

  // optional age computation (for DOB use-case)
  function calculateAge(iso) {
    if (!iso) return null;
    const b = new Date(iso);
    if (Number.isNaN(b.getTime())) return null;
    let age = now.getFullYear() - b.getFullYear();
    const m1 = now.getMonth();
    const d1 = now.getDate();
    if (m1 < b.getMonth() || (m1 === b.getMonth() && d1 < b.getDate())) age--;
    return age;
  }

  const days = getCalendarDays(calendarMonth);
  const displayValue = formatDisplay(value);
  const age = calculateAge(value);

  return (
    <div className={`datepicker-root ${className}`} ref={containerRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {label}
        </label>
      )}

      <div className="relative">
        {/* Visible input-like button */}
        <div
          onClick={() => {
            setOpen((s) => !s);
            setYearMode(false);
          }}
          className="input-field flex items-center justify-between cursor-pointer select-none dark:bg-gray-800 dark:border-gray-700 dark:text-white"
          role="button"
          aria-haspopup="dialog"
          aria-expanded={open}
        >
          <span
            className={!displayValue ? "text-gray-400 dark:text-gray-500" : ""}
          >
            {displayValue || placeholder}
          </span>
          {/* calendar icon (simple) */}
          <CalendarDays className="w-5 h-5 text-gray-400 dark:text-gray-500" />
        </div>

        {/* Popover */}
        {open && (
          <div
            className={`absolute z-50 p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl popup-anim w-72 left-0 ${
              dropUp ? "bottom-full mb-2" : "mt-2"
            }`}
            role="dialog"
            aria-modal="false"
          >
            {/* Header: month/year or year-range (no extra year-mode controls in header) */}
            <div className="flex justify-between items-center mb-3">
              {/* left chevron (acts as year-step in yearMode, month-step otherwise) */}
              <button
                type="button"
                onClick={() => {
                  if (yearMode)
                    setCalendarMonth(
                      new Date(
                        calendarMonth.getFullYear() - 12,
                        calendarMonth.getMonth(),
                        1
                      )
                    );
                  else
                    setCalendarMonth(
                      new Date(
                        calendarMonth.getFullYear(),
                        calendarMonth.getMonth() - 1,
                        1
                      )
                    );
                }}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-gray-600 dark:text-gray-300"
                aria-label="Previous"
              >
                {/* left icon */}
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M15 6 L9 12 L15 18"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              {/* Month / Year label (click toggles year mode) */}
              <span
                className="font-medium text-gray-800 dark:text-gray-100 cursor-pointer hover:text-green-600 dark:hover:text-green-400"
                onClick={() => enterYearMode()}
                role="button"
                tabIndex={0}
              >
                {yearMode
                  ? `${calendarMonth.getFullYear() - 6} – ${calendarMonth.getFullYear() + 5}`
                  : calendarMonth.toLocaleString("default", {
                      month: "long",
                      year: "numeric",
                    })}
              </span>

              {/* right chevron */}
              <button
                type="button"
                onClick={() => {
                  if (yearMode)
                    setCalendarMonth(
                      new Date(
                        calendarMonth.getFullYear() + 12,
                        calendarMonth.getMonth(),
                        1
                      )
                    );
                  else
                    setCalendarMonth(
                      new Date(
                        calendarMonth.getFullYear(),
                        calendarMonth.getMonth() + 1,
                        1
                      )
                    );
                }}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-gray-600 dark:text-gray-300"
                aria-label="Next"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M9 6 L15 12 L9 18"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>

            {/* Year mode: 12 year grid */}
            {yearMode ? (
              <div className="grid grid-cols-3 gap-3">
                {Array.from(
                  { length: 12 },
                  (_, i) => calendarMonth.getFullYear() - 6 + i
                ).map((year) => {
                  const disabled =
                    !allowFuture && year > new Date().getFullYear();
                  const selectedYear =
                    selectedDate && selectedDate.getFullYear() === year;
                  return (
                    <button
                      key={year}
                      type="button"
                      disabled={disabled}
                      onClick={() => {
                        if (disabled) return;
                        setCalendarMonth(
                          new Date(year, calendarMonth.getMonth(), 1)
                        );
                        setYearMode(false);
                      }}
                      className={`py-2 rounded text-sm transition
                        ${disabled ? "text-gray-400 dark:text-gray-600 cursor-not-allowed opacity-50" : "hover:bg-green-100 dark:hover:bg-green-900/30 dark:text-gray-200"}
                        ${selectedYear ? "bg-green-600 text-white hover:!bg-green-700" : ""}`}
                    >
                      {year}
                    </button>
                  );
                })}
              </div>
            ) : (
              <>
                {/* Weekday headings */}
                <div className="grid grid-cols-7 text-center text-xs text-gray-500 dark:text-gray-400 mb-1">
                  {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => (
                    <span key={d}>{d}</span>
                  ))}
                </div>

                {/* Day grid */}
                <div className="grid grid-cols-7 gap-1">
                  {getCalendarDays(calendarMonth).map((day, idx) => {
                    if (day === null) return <span key={idx} />;

                    const thisDate = new Date(
                      calendarMonth.getFullYear(),
                      calendarMonth.getMonth(),
                      day
                    );
                    const futureDisabled =
                      !allowFuture && thisDate > maxDateDefault;
                    const selected = isSelected(day);
                    const today = isToday(day);

                    return (
                      <button
                        key={idx}
                        type="button"
                        disabled={futureDisabled}
                        onClick={() => pickDay(day)}
                        className={`py-1.5 text-sm rounded transition
                          ${futureDisabled ? "text-gray-400 dark:text-gray-600 cursor-not-allowed opacity-40" : "hover:bg-green-100 dark:hover:bg-green-900/30 dark:text-gray-200"}
                          ${selected ? "bg-green-600 text-white hover:!bg-green-700" : ""}`}
                      >
                        <span
                          className={`${today ? "bg-blue-500 text-white inline-block w-6 h-6 leading-6 text-center rounded-full" : "inline-block w-6 h-6 leading-6 text-center"}`}
                        >
                          {day}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            {/* Action bar: Clear & Today */}
            <div className="flex justify-between pt-3 mt-3 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={() => {
                  clearDate();
                }}
                className="text-sm text-gray-600 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition"
              >
                Clear
              </button>

              <button
                type="button"
                onClick={() => {
                  pickToday();
                }}
                className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
              >
                Today
              </button>
            </div>
          </div>
        )}
      </div>

      {/* optional age display */}
      {showAge && value && (
        <p className="text-xs text-gray-500 mt-1">
          Age: {calculateAge(value)} years
        </p>
      )}
    </div>
  );
}
